import * as _formatDate from 'date-fns/format';
import * as ru from 'date-fns/locale/ru';

import { declineByCount } from 'sletat-common-utils/lib/format/declineByCount';
import { numberFormat } from 'sletat-common-utils/lib/format/number';
import { addVersionId } from '../../utils/url';
import { TourStore as TourModel } from '../stores/tour';


export function formatDateDotted(date: Date | string | number): string {
    return _formatDate(date, 'DD.MM.YYYY');
}

export function formatDateSlashed(date: Date | string | number): string {
    return _formatDate(date, 'DD/MM/YYYY');
}

export function formatVisitHotelDate(date: Date): string {
    return _formatDate(date, 'MMMM YYYY', { locale: ru });
}

export function formatShortDay(date: Date): string {
    return _formatDate(date, 'D MMMM, dd', { locale: ru });
}

export function formatDay(date: Date): string {
    return _formatDate(date, 'D', { locale: ru });
}

export function formatVerbalDate(date: Date): string {
    return _formatDate(date, 'D MMMM', { locale: ru });
}

export function formatMonth(date: Date): string {
    return _formatDate(date, 'MMM', { locale: ru });
}

export function shortFormatMonth(date: Date): string {
    const SHORT_MONTH_LENGTH = 3;
    return formatMonth(date).slice(0, SHORT_MONTH_LENGTH);
}

export function formatDayOfWeek(date: Date): string {
    return _formatDate(date, 'dd', { locale: ru });
}

export function formatPriceNumber(price: number): string {
    return numberFormat(Number(price.toFixed(2)));
}


export function getVerbalAge(age: number): string {
    return declineByCount(['год', 'года', 'лет'], age);
}

export function getVerbalHours(hours: number): string {
    return declineByCount(['час', 'часа', 'часов'], hours);
}

export function getVerbalHotelReviews(reviewsCount: number): string {
    return declineByCount(['отзыв', 'отзыва', 'отзывов'], reviewsCount);
}

export function getVerbalNights(nights: number): string {
    return declineByCount(['ночь', 'ночи', 'ночей'], nights);
}

export function getVerbalVariants(variantsAmount: number): string {
    return declineByCount(['предложение', 'предложения', 'предложений'], variantsAmount);
}

export function getVerbalMealTypes(count: number): string {
    return declineByCount(['тип питания', 'типа питания', 'типов питания'], count);
}

export function getVerbalTourOperators(count: number): string {
    return declineByCount(['туроператор', 'туроператора', 'туроператоров'], count);
}

export function getVerbalDepartDates(count: number): string {
    return declineByCount(['дата вылета', 'даты вылета', 'дат вылета'], count);
}

export function getVerbalTourists(count: number, kinds: string[]): string {
    return declineByCount(kinds, count);
}

export function getVerbalAdults(count: number): string {
    return declineByCount(['взрослый', 'взрослых', 'взрослых'], count);
}

export function getVerbalPeople(count: number): string {
    return declineByCount(['человек', 'человека', 'человек'], count);
}

export function getVerbalKids(count: number): string {
    return declineByCount(['ребенок', 'детей', 'детей'], count);
}

export function getVerbalTours(count: number): string {
    return declineByCount(['тур', 'тура', 'туров'], count);
}

export function getVerbalRooms(count: number): string {
    return declineByCount(['номер', 'номера', 'номеров'], count);
}

export function getVerbalTransfersCount(count: number): string {
    return declineByCount(['пересадка', 'пересадки', 'пересадок'], count);
}

export function getVerbalSimilarCountTours(count: number): string {
    return declineByCount(['похожий', 'похожих', 'похожих'], count);
}

export function getVerbalPlacements(count: number): string {
    return declineByCount(['комната', 'комнаты', 'комнат'], count);
}

export function getFormattedTourDetails(tour: TourModel): string {

    const tourLink = `${location.protocol}//${location.host}/tour/${tour.sourceId}-${tour.offerId}-${tour.requestId}/`;

    const descriptionArray: Array<string> = [
        `${tour.cityName} - ${tour.countryName}, ${tour.resortName}`,
        `${tour.hotelName} (${tour.hotelCategoryName})`,
        `${tour.roomType}`,
        `${tour.accommodationCode} (${tour.accommodationName})`,
        `${tour.mealCode} (${tour.mealName})`,
        `${tour.nights} ${getVerbalNights(tour.nights)}`,
        `${tour.departDate && formatDateDotted(tour.departDate)} — ${tour.arrivalDate && formatDateDotted(tour.arrivalDate)} = ${tour.actualPrice} ${tour.actualizationCurrencyAlias}`
    ];

    if (tour.includedServices.length) {
        descriptionArray.push('Включено: ' + tour.includedServices.map(service => service.name).join(', '));
    }

    if (tour.notIncludedServices.length) {
        descriptionArray.push('Не включено: ' + tour.notIncludedServices.map(service => service.name).join(', '));
    }

    return descriptionArray.join(' | ') + ` | Тур: ${addVersionId(tourLink)} | Отель: ${location.href}`;
}

export function getFormattedYoutubeLink(rawLink: string | null): string | null {
    if (!rawLink) {
        return null;
    }
    try {
        const link = fixWebLink(rawLink);
        const videoId = searchForVideoId(link);
        if (!videoId) {
            return null;
        }
        return `https://www.youtube.com/embed/${videoId}`;
    } catch (err: unknown) {
        console.error(err);
        return null;
    }
}

function fixWebLink(link: string): string {
    if (!link.includes('https://') && !link.includes('http://')) {
        return `https://${link}`;
    }
    return link;
}

function searchForVideoId(link: string): string | null {
    if (link.includes('www.youtube.com')) {
        const urlParams = new URL(link);
        return urlParams.searchParams.get('v');
    } else if (link.includes('youtu.be')) {
        return link.split('youtu.be/')[1];
    } else {
        return null;
    }
}

export function getMealCodeLabel(mealName?: string | null, mealCode?: string | null): string {
    return `${mealName}${mealCode && mealCode !== mealName ? `, ${mealCode}` : ''}` || '';
}
